import { useCallback, useSyncExternalStore } from 'react';

export interface Prefs {
  /** On-duty flag shown on the Today hero (device-local driver preference). */
  onDuty: boolean;
  notifyAssignments: boolean;
  notifyMessages: boolean;
}

const KEY = 'uroute.driver.prefs';
const DEFAULTS: Prefs = { onDuty: true, notifyAssignments: true, notifyMessages: true };

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

let snapshot: Prefs = load();
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

export function getPrefs(): Prefs {
  return snapshot;
}

export function setPrefs(patch: Partial<Prefs>): void {
  snapshot = { ...snapshot, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(snapshot));
  } catch {
    /* private mode ??? keep in memory */
  }
  emit();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}

export function useTogglePref(key: keyof Prefs): [boolean, () => void] {
  const prefs = usePrefs();
  const toggle = useCallback(() => setPrefs({ [key]: !prefs[key] } as Partial<Prefs>), [key, prefs]);
  return [prefs[key], toggle];
}
